import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import MyLocationOutlined from '@mui/icons-material/MyLocationOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import TravelExploreOutlined from '@mui/icons-material/TravelExploreOutlined';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import { selectDrawerOpen, selectPanel } from '../../store/selectors/index.js';
import { drawerSet, panelChanged, type PanelId } from '../../store/slices/uiSlice.js';
import CatalogPanel from '../catalog/CatalogPanel.js';
import LayerListPanel from '../layerList/LayerListPanel.js';
import LocatePanel from '../search/LocatePanel.js';
import SearchPanel from '../search/SearchPanel.js';

/**
 * The left panel.
 *
 * Search / Explore / Locate mirror the three tabs the current OKMaps viewer
 * uses, in the same order, because existing users navigate by muscle memory and
 * there is no upside to relearning. Layers is added for the active map stack,
 * which the current viewer buries in a floating window.
 */
const TABS: ReadonlyArray<{ id: PanelId; label: string; icon: React.ReactElement }> = [
  { id: 'search', label: 'Search', icon: <SearchOutlined fontSize="small" /> },
  { id: 'explore', label: 'Explore', icon: <TravelExploreOutlined fontSize="small" /> },
  { id: 'locate', label: 'Locate', icon: <MyLocationOutlined fontSize="small" /> },
  { id: 'layers', label: 'Layers', icon: <LayersOutlined fontSize="small" /> },
];

interface Props {
  readonly width: number;
  readonly compact: boolean;
}

export default function SidePanel({ width, compact }: Props) {
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectDrawerOpen);
  const panel = useAppSelector(selectPanel);

  const content = (
    <Box sx={{ width, maxWidth: '100vw', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {compact && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <IconButton
            size="small"
            onClick={() => dispatch(drawerSet(false))}
            aria-label="Close the data panel and show the map"
            sx={{ m: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Tabs
        value={panel}
        onChange={(_, v: PanelId) => dispatch(panelChanged(v))}
        variant={compact ? 'scrollable' : 'fullWidth'}
        scrollButtons={false}
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 46 }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.id}
            value={t.id}
            label={t.label}
            icon={t.icon}
            iconPosition="start"
            id={`panel-tab-${t.id}`}
            aria-controls={`panel-${t.id}`}
            sx={{ minHeight: 46, fontSize: '0.8125rem' }}
          />
        ))}
      </Tabs>

      <Box
        role="tabpanel"
        id={`panel-${panel}`}
        aria-labelledby={`panel-tab-${panel}`}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        {panel === 'search' && <SearchPanel />}
        {panel === 'explore' && <CatalogPanel />}
        {panel === 'locate' && <LocatePanel />}
        {panel === 'layers' && <LayerListPanel />}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={compact ? 'temporary' : 'persistent'}
      open={open}
      onClose={() => dispatch(drawerSet(false))}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          position: compact ? 'fixed' : 'relative',
          width,
          maxWidth: '100vw',
          borderRight: 1,
          borderColor: 'divider',
          backgroundImage: 'none',
          // Sit below the header rather than over it, so the wordmark and the
          // theme and account controls stay reachable while the panel is open.
          ...(compact ? { top: 'var(--okmaps-header-height, 48px)', height: 'auto', bottom: 0 } : {}),
        },
      }}
    >
      {content}
    </Drawer>
  );
}
